import * as _ from 'lodash'
import React, { useState, useContext } from 'react'
import { t, Trans } from '@lingui/macro'
import { SortableContainer, SortableElement } from 'react-sortable-hoc'
import TopicElementsManager from './TopicElementsManager'
import classNames from 'classnames'
import GeneralRemoveModal from '../../components/common/general-remove-modal/GeneralRemoveModal'
import { useTopicForEditQuery } from './TopicEditPage'

import { AddNewElementButtonModal } from './components/AddNewElementButtonModal'
import { TopicElementModelAsEditor } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { TopicEditingContext } from './TopicEditContext'

const MultipageNavPanel = ({
  currentElement,
  selectElement
}: {
  currentElement: TopicElementModelAsEditor | null
  selectElement: (element: number | null) => void
}) => {
  const [removalModalVisible, showRemovalModal] = useState(false)
  const { topic, setCurrentElementIndex } = useContext(TopicEditingContext)
  const { refetch: refetchTopic } = useTopicForEditQuery()

  // clone because MultipageNavPanel does some optimistic UI which needs to mutate this (frozen) array
  const elements = _.cloneDeep(topic.draftVersion?.elements || [])

  const ElementList = SortableContainer(
    ({ elements }: { elements: TopicElementModelAsEditor[] }) => {
      return (
        <span>
          {elements.map((element, index) => (
            <Element
              key={index}
              element={element}
              elementIndex={index}
              index={index}
            />
          ))}
        </span>
      )
    }
  )

  const Element = SortableElement(
    ({
      element,
      elementIndex
    }: {
      element: TopicElementModelAsEditor
      elementIndex: number
    }) => {
      return (
        <li
          key={elementIndex}
          className="page-thumbnail"
          onClick={() => {
            selectElement(elementIndex)
          }}
        >
          <div className="page-thumbnail-switch">
            <div
              className={classNames('page-thumbnail-item', {
                active: currentElement && currentElement.id === element.id
              })}
            >
              <span
                className={`icon icon-element-${element.type.toLowerCase()}`}
              ></span>

              <div className="label">{elementIndex + 1}</div>

              {TopicElementsManager.canMoveUp(topic, element) && ( // TODO add disable when loading
                <a
                  onClick={async (event) => {
                    event.stopPropagation()

                    const newIndex = elementIndex - 1
                    await TopicElementsManager.onMoveUp(topic, element)

                    await refetchTopic()
                    setCurrentElementIndex(newIndex)
                  }}
                  className="move-prev"
                  title={t`Shift page left`}
                >
                  &lt;
                </a>
              )}
              {TopicElementsManager.canMoveDown(topic, element) && ( // TODO add disable when loading
                <a
                  onClick={async (event) => {
                    event.stopPropagation()

                    const newIndex = elementIndex + 1
                    await TopicElementsManager.onMoveDown(topic, element)

                    await refetchTopic()
                    setCurrentElementIndex(newIndex)
                  }}
                  className="move-next"
                  title={t`Shift page right`}
                >
                  &gt;
                </a>
              )}
            </div>
          </div>
          {currentElement && currentElement.id === element.id && (
            <div className="pointer"></div>
          )}
        </li>
      )
    }
  )

  return (
    <div className="pages-nav">
      {currentElement && (
        <div className="page-trash">
          <GeneralRemoveModal
            show={removalModalVisible}
            entityName={t`element`}
            confirmButton={t`Delete`}
            onDeleted={() => showRemovalModal(false)}
            onCanceled={() => showRemovalModal(false)}
            operation={async () => {
              if (!currentElement) return

              await TopicElementsManager.onRemove(topic, currentElement)
              const elements = topic.draftVersion?.elements
              if (elements) {
                const elementIndex = elements.length - 2
                selectElement(elementIndex >= 0 ? elementIndex : null)
              }
              refetchTopic()
            }}
          />
          <div className="divRecycle page-thumbnail">
            <div className="page-thumbnail-item">
              <span
                className="icon icon-trash"
                onClick={() => showRemovalModal(true)}
              ></span>
            </div>
          </div>
        </div>
      )}

      <ul className="pages-wrapper">
        <li className="page-thumbnail page-type-system-generated-title">
          <div className="page-thumbnail-switch">
            <div
              onClick={() => selectElement(null)}
              className={classNames('page-thumbnail-item', {
                active: !currentElement
              })}
            >
              <span className="icon icon-element-system-generated-title"></span>

              <div className="label">
                <Trans>Cover</Trans>
              </div>
            </div>
          </div>
          {!currentElement && <div className="pointer"></div>}
        </li>

        <ElementList
          key={JSON.stringify(elements)}
          axis="x"
          pressDelay={140}
          elements={elements}
          onSortEnd={async ({ oldIndex, newIndex }) => {
            const fromElement = elements[oldIndex]

            const toElement = elements[newIndex]
            const toOrder = toElement.order

            if (fromElement && toElement) {
              if (toElement.order > fromElement.order) {
                await TopicElementsManager.onDragDown(
                  topic,
                  fromElement,
                  toOrder
                )
              } else {
                await TopicElementsManager.onDragUp(topic, fromElement, toOrder)
              }
              await refetchTopic()

              selectElement(newIndex)
            }
          }}
          helperClass={'dragged'}
        />
        <AddNewElementButtonModal />
      </ul>
    </div>
  )
}

export default MultipageNavPanel
